import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Shield, Zap, FileText, Settings } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth.js';

interface OversightSidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  userRole?: string;
  isLimitedPartner?: boolean;
  isAdmin?: boolean;
}

const OversightSidebar: React.FC<OversightSidebarProps> = ({ 
  activeTab, 
  onTabChange, 
  userRole: _userRole = 'admin',
  isLimitedPartner = false,
  isAdmin = false
}) => {
  const { hasRole } = useAuth();
  const hasAdminRole = hasRole('admin') || hasRole('fund_manager');

  const lpTabs = [
    { id: 'onboarding', label: 'LP Onboarding', icon: TrendingUp, adminOnly: false },
    { id: 'overview', label: 'Overview Dashboard', icon: Shield, adminOnly: false },
    { id: 'performance', label: 'Performance & Risk', icon: Zap, adminOnly: false }
  ];

  const fullTabs = [
    { id: 'overview', label: 'Overview Dashboard', icon: TrendingUp, adminOnly: false },
    { id: 'performance', label: 'Performance & Risk', icon: Shield, adminOnly: false },
    { id: 'strategy', label: 'Strategy & Attribution', icon: Zap, adminOnly: true },
    { id: 'audit', label: 'Audit & Compliance', icon: FileText, adminOnly: true },
    ...(isAdmin ? [{ id: 'admin', label: 'Workspace Management', icon: Settings, adminOnly: true }] : [])
  ];

  const tabs = isLimitedPartner ? lpTabs : fullTabs;

  return (
    <div className="w-64 h-full bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 border-r border-yellow-500/20 relative z-20">
      <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 to-transparent" />
      
      <div className="relative z-30 p-6">
        <div className="mb-8 text-center relative z-40">
          <img 
            src="/assets/arcoversight-logo.png" 
            alt="arcOversight" 
            className="h-48 mx-auto mb-4 relative z-50"
            style={{ maxWidth: '480px', objectFit: 'contain' }}
          />
        </div>
        
        <nav className="space-y-2">
          {tabs.map((tab) => {
            if (tab.adminOnly && (!hasAdminRole || isLimitedPartner)) return null;
            return (
              <motion.button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full flex items-center px-4 py-3 rounded-lg transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 text-yellow-400 border border-yellow-500/30 shadow-lg'
                    : 'text-slate-300 hover:bg-slate-700/50 hover:text-white hover:border-slate-600/30 border border-transparent'
                }`}
              >
                <tab.icon className="w-5 h-5 mr-3" />
                <span className="font-medium">{tab.label}</span>
                {activeTab === tab.id && (
                  <div className="ml-auto w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
                )}
              </motion.button>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default OversightSidebar;
